import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import PaxCertifiedBadge from '../components/PaxCertifiedBadge';
import './PilotPublicProfilePage.css';

function formatHours(value, suffix = 'h') {
  if (value == null) return '—';
  return `${Number(value).toLocaleString()} ${suffix}`;
}

export default function PilotPublicProfilePage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    axios
      .get(`/api/pilots/${id}/public`)
      .then((res) => {
        if (!cancelled) setProfile(res.data.profile);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.error || t('pilotProfile.loadError'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  if (loading) {
    return (
      <div className="container pilot-profile-page">
        <p>{t('pilotProfile.loading')}</p>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="container pilot-profile-page">
        <p className="error">{error || t('pilotProfile.notFound')}</p>
        <Link to="/booking" className="btn btn-primary">
          {t('pilotProfile.backToBooking')}
        </Link>
      </div>
    );
  }

  const loc = i18n.language === 'hu' ? 'hu-HU' : i18n.language === 'he' ? 'he-IL' : 'en-GB';

  return (
    <div className="container pilot-profile-page">
      <nav className="pilot-profile-breadcrumb">
        <Link to="/booking">{t('pilotProfile.backToBooking')}</Link>
      </nav>

      <header className="pilot-profile-header card">
        <h1>{profile.name}</h1>
        {profile.pax_certified && (
          <p className="pilot-profile-badge-row">
            <PaxCertifiedBadge pilotName={profile.name} showTooltip inline />
          </p>
        )}
        <p className="pilot-profile-intro">{t('pilotProfile.intro')}</p>
      </header>

      <section className="card pilot-profile-section">
        <h2>{t('pilotProfile.experienceTitle')}</h2>
        <dl className="pilot-profile-dl">
          <dt>{t('pilotProfile.totalHours')}</dt>
          <dd>{formatHours(profile.experience?.total_flight_hours)}</dd>
          <dt>{t('pilotProfile.recentHours')}</dt>
          <dd>{formatHours(profile.experience?.recent_flight_hours_12m)}</dd>
        </dl>
      </section>

      <section className="card pilot-profile-section">
        <h2>{t('pilotProfile.qualificationsTitle')}</h2>
        <dl className="pilot-profile-dl">
          <dt>{t('pilotProfile.licence')}</dt>
          <dd>{profile.qualifications?.licence || t('pilotProfile.pending')}</dd>
          <dt>{t('pilotProfile.ratings')}</dt>
          <dd>{profile.qualifications?.ratings || t('pilotProfile.pending')}</dd>
          {profile.qualifications?.sep_valid_until && (
            <>
              <dt>{t('pilotProfile.sepValid')}</dt>
              <dd>{profile.qualifications.sep_valid_until}</dd>
            </>
          )}
        </dl>
      </section>

      <section className="card pilot-profile-section">
        <h2>{t('pilotProfile.aircraftTitle')}</h2>
        <dl className="pilot-profile-dl">
          <dt>{t('pilotProfile.aircraftUsed')}</dt>
          <dd>{profile.aircraft?.primary || profile.aircraft?.type || t('pilotProfile.pending')}</dd>
          {profile.aircraft?.additional_types?.length > 0 && (
            <>
              <dt>{t('pilotProfile.otherTypes')}</dt>
              <dd>{profile.aircraft.additional_types.join(', ')}</dd>
            </>
          )}
          <dt>{t('pilotProfile.hoursOnType')}</dt>
          <dd>{formatHours(profile.aircraft?.hours_on_type)}</dd>
        </dl>
      </section>

      <p className="pilot-profile-footnote">{t('pilotProfile.footnote')}</p>
    </div>
  );
}
